import { UploadSimple, File as FileIcon, X } from "@phosphor-icons/react";
import { forwardRef, useRef, useState, type DragEvent, type HTMLAttributes } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { twRing } from "@/lib/emotionTw";

export type FileDropzoneProps = Omit<HTMLAttributes<HTMLDivElement>, "onDrop"> & {
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  error?: boolean;
  title?: string;
  description?: string;
  browseLabel?: string;
  files?: File[];
  onFilesChange?: (files: File[]) => void;
};

interface FileDropzoneOwnerState {
  dragActive: boolean;
  error: boolean;
  disabled: boolean;
}

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline.
 *
 * Cascade notes (tailwind-merge kept the LAST class of each conflicting group
 * in the cn() list, so the branch order below models the net winners):
 * - border-color: error beats dragActive beats the default branch.
 * - background:   disabled (surface-muted) beats error (danger-soft/40) beats
 *   dragActive (primary-soft) beats the base surface-card.
 * - cursor:       disabled cursor-not-allowed beats the base cursor-pointer.
 * - On the description span, `bfrs-leading-5` [pos 49092] beats the line-height
 *   from `bfrs-text-xs` [pos 48728] — net line-height 1.25rem. */

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// bfrs-sr-only
const FileDropzoneHiddenInput = styled("input", {
  label: "AgenticFileDropzone-input"
})({
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  borderWidth: 0
});

const FileDropzoneListRoot = styled("div", {
  label: "AgenticFileDropzone-listRoot",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: FileDropzoneOwnerState }>(({ ownerState }) => [
  {
    overflow: "hidden",
    borderRadius: "var(--bfrs-radius-xl)",
    borderWidth: "1px",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s"
  },
  ownerState.dragActive
    ? {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))",
        "--tw-bg-opacity": "1",
        backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
      }
    : {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))"
      },
  ownerState.error && {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
  },
  ownerState.disabled && { opacity: ".7" }
]);

const FileDropzoneFileRow = styled("div", {
  label: "AgenticFileDropzone-fileRow",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { divided: boolean } }>(({ ownerState }) => [
  {
    display: "flex",
    alignItems: "center",
    gap: ".75rem",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    paddingTop: ".75rem",
    paddingBottom: ".75rem"
  },
  ownerState.divided && {
    borderTopWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))"
  }
]);

const FileDropzoneFileIcon = styled("span", {
  label: "AgenticFileDropzone-fileIcon"
})({
  display: "flex",
  width: "2rem",
  height: "2rem",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--bfrs-radius-lg)",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
});

const FileDropzoneFileInfo = styled("span", {
  label: "AgenticFileDropzone-fileInfo"
})({
  minWidth: "0px",
  flex: "1 1 0%"
});

const FileDropzoneFileName = styled("span", {
  label: "AgenticFileDropzone-fileName"
})({
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: ".75rem",
  lineHeight: "1rem",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const FileDropzoneFileSize = styled("span", {
  label: "AgenticFileDropzone-fileSize"
})({
  fontSize: "11px",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const FileDropzoneRemoveButton = styled("button", {
  label: "AgenticFileDropzone-removeButton"
})({
  display: "flex",
  width: "1.5rem",
  height: "1.5rem",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--bfrs-radius-md)",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&:hover": {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-danger-soft) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
  },
  "&:focus-visible": {
    ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / var(--tw-ring-opacity, 1))"),
    "--tw-ring-opacity": "1"
  }
});

const FileDropzoneAddButton = styled("button", {
  label: "AgenticFileDropzone-addButton"
})({
  display: "flex",
  width: "100%",
  cursor: "pointer",
  alignItems: "center",
  justifyContent: "center",
  gap: ".5rem",
  borderTopWidth: "1px",
  borderStyle: "dashed",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: ".625rem",
  paddingBottom: ".625rem",
  fontSize: ".75rem",
  lineHeight: "1rem",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&:hover": {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
  },
  "&:focus-visible": {
    ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / var(--tw-ring-opacity, 1))"),
    "--tw-ring-opacity": "1"
  }
});

const dropzoneBase: CSSObject = {
  display: "flex",
  minHeight: "9rem",
  cursor: "pointer",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--bfrs-radius-xl)",
  borderWidth: "1px",
  borderStyle: "dashed",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  paddingLeft: "1.25rem",
  paddingRight: "1.25rem",
  paddingTop: "1.5rem",
  paddingBottom: "1.5rem",
  textAlign: "center",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&:focus-within": twRing("2px", "hsl(var(--bfrs-color-border-focus) / .2)")
};

const FileDropzoneRoot = styled("div", {
  label: "AgenticFileDropzone-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: FileDropzoneOwnerState }>(({ ownerState }) => [
  dropzoneBase,
  ownerState.dragActive
    ? {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))",
        "--tw-bg-opacity": "1",
        backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
      }
    : {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-form-control-border) / var(--tw-border-opacity, 1))"
      },
  ownerState.error && {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
    backgroundColor: "hsl(var(--bfrs-color-danger-soft) / .4)"
  },
  ownerState.disabled && {
    cursor: "not-allowed",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
    opacity: ".7"
  }
]);

const FileDropzoneUploadIcon = styled("span", {
  label: "AgenticFileDropzone-uploadIcon"
})({
  marginBottom: ".75rem",
  display: "flex",
  width: "2.5rem",
  height: "2.5rem",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--bfrs-radius-pill)",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
});

const FileDropzoneTitle = styled("span", {
  label: "AgenticFileDropzone-title"
})({
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const FileDropzoneDescription = styled("span", {
  label: "AgenticFileDropzone-description"
})({
  marginTop: ".25rem",
  maxWidth: "24rem",
  fontSize: ".75rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
});

const FileDropzoneBrowseLabel = styled("span", {
  label: "AgenticFileDropzone-browseLabel"
})({
  marginTop: ".75rem",
  fontSize: ".75rem",
  lineHeight: "1rem",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
});

export const FileDropzone = forwardRef<HTMLDivElement, FileDropzoneProps>(
  ({ className, accept, multiple, disabled, error, title = "Drop files here", description = "Drag and drop files, or browse from your device.", browseLabel = "Browse files", files, onFilesChange, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragActive, setDragActive] = useState(false);

    const hasFiles = Boolean(files && files.length > 0);
    const ownerState: FileDropzoneOwnerState = {
      dragActive,
      error: Boolean(error),
      disabled: Boolean(disabled)
    };

    const commitFiles = (incoming: FileList | File[]) => {
      if (disabled) return;
      const next = Array.from(incoming);
      onFilesChange?.(multiple ? [...(files ?? []), ...next] : next.slice(0, 1));
    };

    const removeFile = (file: File) => {
      onFilesChange?.((files ?? []).filter((f) => f !== file));
    };

    const handleDrag = (event: DragEvent, active: boolean) => {
      event.preventDefault();
      event.stopPropagation();
      if (!disabled) setDragActive(active);
    };

    const openPicker = () => {
      if (!disabled) {
        if (inputRef.current) inputRef.current.value = "";
        inputRef.current?.click();
      }
    };

    const hiddenInput = (
      <FileDropzoneHiddenInput
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={(event) => {
          if (event.currentTarget.files) commitFiles(event.currentTarget.files);
        }}
      />
    );

    if (hasFiles) {
      return (
        <FileDropzoneListRoot
          ref={ref}
          ownerState={ownerState}
          className={className}
          onDragEnter={(e) => handleDrag(e, true)}
          onDragOver={(e) => handleDrag(e, true)}
          onDragLeave={(e) => handleDrag(e, false)}
          onDrop={(e) => { handleDrag(e, false); commitFiles(e.dataTransfer.files); }}
          {...props}
        >
          {hiddenInput}
          {files!.map((file, index) => (
            <FileDropzoneFileRow key={`${file.name}-${index}`} ownerState={{ divided: index > 0 }}>
              <FileDropzoneFileIcon>
                <FileIcon size={16} weight="bold" />
              </FileDropzoneFileIcon>
              <FileDropzoneFileInfo>
                <FileDropzoneFileName>{file.name}</FileDropzoneFileName>
                <FileDropzoneFileSize>{formatFileSize(file.size)}</FileDropzoneFileSize>
              </FileDropzoneFileInfo>
              {!disabled && (
                <FileDropzoneRemoveButton
                  type="button"
                  aria-label={`Remove ${file.name}`}
                  onClick={(e) => { e.stopPropagation(); removeFile(file); }}
                >
                  <X size={13} weight="bold" />
                </FileDropzoneRemoveButton>
              )}
            </FileDropzoneFileRow>
          ))}
          {multiple && !disabled && (
            <FileDropzoneAddButton type="button" onClick={openPicker}>
              <UploadSimple size={13} weight="bold" />
              <span>Add more files</span>
            </FileDropzoneAddButton>
          )}
        </FileDropzoneListRoot>
      );
    }

    return (
      <FileDropzoneRoot
        ref={ref}
        ownerState={ownerState}
        className={className}
        onClick={openPicker}
        onDragEnter={(e) => handleDrag(e, true)}
        onDragOver={(e) => handleDrag(e, true)}
        onDragLeave={(e) => handleDrag(e, false)}
        onDrop={(e) => { handleDrag(e, false); commitFiles(e.dataTransfer.files); }}
        {...props}
      >
        {hiddenInput}
        <FileDropzoneUploadIcon>
          <UploadSimple size={20} weight="bold" />
        </FileDropzoneUploadIcon>
        <FileDropzoneTitle>{title}</FileDropzoneTitle>
        <FileDropzoneDescription>{description}</FileDropzoneDescription>
        <FileDropzoneBrowseLabel>{browseLabel}</FileDropzoneBrowseLabel>
      </FileDropzoneRoot>
    );
  }
);

FileDropzone.displayName = "FileDropzone";
